import {
  Button,
  ButtonVariants,
  IError,
  LoadingOverlay,
  Spinner,
  Vendors,
  useToast,
} from '@comptia-sso/core';
import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { RedeemModal } from '../RedeemModal';
import { CertMasterPlatformRedemption } from '../CertMasterPlatformRedemption';
import { CertMasterPlatformRedemptionProvider } from '../CertMasterPlatformRedemption/CertMasterPlatformRedemptionContext';
import { InformationText } from './InformationText';
import { CertMasterPlatformLinkAccountProvider } from '../CertMasterPlatformLinkAccount/CertMasterPlatformLinkAccountContext';
import { CertMasterPlatformLinkAccount } from '../CertMasterPlatformLinkAccount';

// Config.
import { env } from 'config';

// Hooks.
import {
  useProfile,
  useProfileEntitlements,
  useRedirectToVendor,
  useTrackEvent,
  useTrackOdpEvent,
} from 'hooks';

// Icons.
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import LaptopMacIcon from '@material-ui/icons/LaptopMac';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

// Images.

// Styles.
import styles from './AccessYourTraining.module.scss';

export const AccessYourTraining = (): ReactElement => {
  const [showTooltipId, setShowTooltipId] = useState('');
  const { t } = useTranslation();
  const [popToast] = useToast();
  const [
    profile,
    {
      error: profileError,
      isLoading: isLoadingProfile,
      refetch: refetchProfile,
    },
  ] = useProfile();
  const [
    entitlements,
    {
      error: entitlementsError,
      isLoading: isLoadingEntitlements,
      refetch: refetchEntitlements,
    },
  ] = useProfileEntitlements(profile?.id);
  const trackEvent = useTrackEvent('Access Your Training');
  const trackOdpEvent = useTrackOdpEvent();
  const [redirectToVendor, { isLoading: isRedirecting }] = useRedirectToVendor(
    profile?.id,
  );
  const [showRedeemModal, setShowRedeemModal] = useState(false);

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    if (showTooltipId === target.id) {
      setShowTooltipId('');
    } else {
      setShowTooltipId(target.id); // Toggle tooltip visibility
    }
  };

  const handleFocus = (event: FocusEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    if (showTooltipId === target.id) {
      setShowTooltipId('');
    } else {
      setShowTooltipId(target.id); // Toggle tooltip visibility
    }
  };

  const handleOutsideClick = (event: MouseEvent | Event) => {
    // Close the tooltip if clicking outside the icon or on the icon
    const target = event.target as HTMLElement;
    if (!target.closest(`.${styles.toolTipContainer}`)) {
      setShowTooltipId('');
    }
  };

  const handleFocusLeave = (event: FocusEvent | Event) => {
    // Close the tooltip if clicking outside the icon or on the icon
    const target = event.target as HTMLElement;
    if (target.closest(`.${styles.toolTipContainer}`)) {
      setShowTooltipId('');
    }
  };

  useEffect(() => {
    // Add event listener for clicks outside the tooltip
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('focusout', handleFocusLeave);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.addEventListener('focusout', handleFocusLeave);
    };
  }, []);

  const renderUserEntitlements = () => {
    return renderEntitlements('active');
  };

  const renderEntitlements = (group: string) =>
    entitlements
      ?.sort(function (a, b) {
        return group === 'active'
          ? new Date(b.redemptionDate).valueOf() -
              new Date(a.redemptionDate).valueOf()
          : new Date(b.expirationDate).valueOf() -
              new Date(a.expirationDate).valueOf();
      })
      .map((entitlement, index) => {
        const expirationDate = new Date(entitlement.expirationDate);
        const isActive =
          expirationDate >= new Date() ||
          (!entitlement.expirationDate &&
            !entitlement.redemptionDate &&
            entitlement.vendor === Vendors.BenchPrep);

        return (
          group === 'active' &&
          isActive && (
            <li key={`${entitlement.product.title}-${index}`}>
              <LaptopMacIcon />
              <span>
                <span>{entitlement.product?.title}</span>
                {entitlement.migratedToProductTitle !== '' &&
                  entitlement.migratedToProductTitle !== null && (
                    <span
                      id={`tooltip-${index}`}
                      onMouseDown={(e) => handleClick(e)}
                      onFocus={(e) => handleFocus(e)}
                      className={styles.toolTipContainer}
                      role="button"
                      tabIndex={0}
                    >
                      <ReportProblemOutlinedIcon />
                      {showTooltipId === `tooltip-${index}` && (
                        <span className={styles.toolTip}>
                          {t('AccessYourTraining.MigrateContent1')}{' '}
                          {entitlement.migratedToProductTitle}.{' '}
                          {t('AccessYourTraining.MigrateContent2')}{' '}
                          {entitlement.product.title}{' '}
                          {t('AccessYourTraining.MigrateContent3')}{' '}
                          {entitlement.migratedToProductTitle}.
                        </span>
                      )}
                    </span>
                  )}
              </span>
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  trackEvent('Clicked', { label: entitlement.product?.title });
                  trackOdpEvent(
                    'navigation',
                    'Launchpad Training - My Training',
                    entitlement.product?.title,
                  );
                  try {
                    await redirectToVendor(entitlement.vendor, {
                      classroom: entitlement.product?.classroom,
                      productCode: entitlement.product?.vendorProductCode,
                      version: entitlement.preferredProductVersion,
                    });
                  } catch (error) {
                    popToast(
                      (error as IError)?.message || t('Toast.Error.Default'),
                    );
                  }
                }}
              >
                {t('AccessYourTraining.Study')} <ArrowForwardIcon />
              </a>
            </li>
          )
        );
      })
      .filter(Boolean);

  const handleEvent = (e: MouseEvent<HTMLElement>) => {
    const element = e.currentTarget as HTMLElement;
    trackEvent('Clicked', { label: element.innerText });
    trackOdpEvent('navigation', 'Launchpad Training - Home', element.innerText);
  };

  useEffect(() => {
    if (entitlementsError) {
      popToast(
        (entitlementsError as IError)?.message || t('Toast.Error.Default'),
      );
    }
  }, [entitlementsError]);

  return (
    <CertMasterPlatformRedemptionProvider>
      <CertMasterPlatformLinkAccountProvider>
        <RedeemModal
          onClose={() => setShowRedeemModal(false)}
          open={showRedeemModal}
        />
        <CertMasterPlatformRedemption />
        <CertMasterPlatformLinkAccount
          refreshEntitlements={() => {
            refetchEntitlements();
            refetchProfile();
          }}
        />
        <div className={styles.container}>
          <div className={styles.title}>
            <h2>{t('AccessYourTraining.Title')}</h2>
            <Button
              variant={ButtonVariants.Secondary}
              onClick={() => setShowRedeemModal(true)}
            >
              {t('AccessYourTraining.Redeem')}
            </Button>
          </div>
          {!isLoadingEntitlements && entitlements.length === 0 && (
            <div className={styles.noCourses}>
              <Trans
                i18nKey="InfoText.NoCourses"
                components={{
                  linktag: (
                    <a role="button" onClick={() => setShowRedeemModal(true)} />
                  ),
                }}
              />
            </div>
          )}
          <div className={styles.accessYourTraining}>
            {env.FLAG_LEARNING_SYSTEMS_ENABLED ? (
              <div className={styles.accessYourTrainingDetails}>
                <div className={styles.accessYourTrainingTop}>
                  <div className={styles.external}>
                    <div>
                      {!isLoadingProfile &&
                        !isLoadingEntitlements &&
                        entitlements.length === 0 && (
                          <Button
                            className={styles.button}
                            link
                            onClick={handleEvent}
                            to="https://store.comptia.org"
                          >
                            {t('AccessYourTraining.Button')}
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
                <div className={styles.accessYourTrainingBottom}>
                  {isLoadingEntitlements ? (
                    <Spinner className={styles.spinner} />
                  ) : (
                    <ul className={styles.linksList}>
                      {entitlements.length > 0 && renderUserEntitlements()}
                    </ul>
                  )}
                </div>
                <div className={styles.accessYourTrainingMissingCourses}>
                  <InformationText profile={profile} />
                </div>
              </div>
            ) : (
              <div className={styles.accessYourTrainingComingSoon}>
                <h2>{t('AccessYourTraining.Title')}</h2>
                <Button
                  className={styles['coming-soon__button']}
                  link
                  to="https://www.comptia.org/training"
                >
                  {t('ComingSoon.BrowseTraining')}
                </Button>
                <div className={styles.accessYourTrainingComingSoonContent}>
                  <p>
                    <strong>{t('ComingSoon.ReleaseDate')}</strong>
                  </p>
                  <p>{t('ComingSoon.AccessYourTraining')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <LoadingOverlay isOpen={isRedirecting} text={t('Loading.Default')} />
      </CertMasterPlatformLinkAccountProvider>
    </CertMasterPlatformRedemptionProvider>
  );
};
